"use client";
import {
  Button,
  FormError,
  formErrorMessage,
  FormGroup,
  Input,
  Label,
} from "cy-fe";
import { formControlColorState } from "cy-fe/src/components/Forms/reactHookForm";
import { BodyContainer } from "cy-remittance/components/layouts";
import { Controller, useForm } from "react-hook-form";
import { ListBox, ListBoxLabel } from "cy-fe/src/components/Forms/ListBox";
import {
  afterCurrentDateFilter,
  beforeCurrentDateFilter,
  DatePicker,
} from "cy-fe/src/components/Forms/DatePicker";
import { Country } from "cy-api/simple-payments/countries/models";
import { IdentificationType, IdentificationFormInputs } from "./models";
import { useMemo } from "react";
import { cn } from "cy-fe/src/utils/cn";
import { IssuingCountryComboBox } from "./IssuingCountryComboBox";

export function IdentificationForm<TType extends number = number>({
  onSubmit,
  countries,
  identificationTypes,
  className,
}: {
  onSubmit: (values: IdentificationFormInputs) => Promise<void>;
  countries: Country[];
  identificationTypes: IdentificationType<TType>[];
  className?: string;
}) {
  const countryOptions = useMemo(
    () =>
      countries.map((x) => ({
        id: x.country_code,
        name: x.name,
        countryCode: x.country_code,
      })),
    [countries],
  );
  const typeOptions = useMemo(
    () => identificationTypes.map((x) => ({ id: x.id, name: x.name })),
    [identificationTypes],
  );
  const typeOptionsMap = useMemo(
    () =>
      typeOptions.reduce<Record<string, (typeof typeOptions)[number]>>(
        (acc, x) => {
          acc[x.id] = x;
          return acc;
        },
        {},
      ),
    [typeOptions],
  );

  const form = useForm<IdentificationFormInputs>({});
  async function submitHandler(values: IdentificationFormInputs) {
    await onSubmit(values);
  }
  const idType = form.watch("type");
  const type = useMemo(
    () => identificationTypes.find((x) => x.id === idType),
    [identificationTypes, idType],
  );
  const showIssueDate =
    type?.issueDate === "required" || type?.issueDate === "optional";
  const showExpiryDate =
    type?.expiryDate === "required" || type?.expiryDate === "optional";
  const showIssuingCountry =
    type?.issuingCountry === "required" || type?.issuingCountry === "optional";
  const showTempResidenceVisa =
    type?.tempResidenceVisa === "required" || type?.tempResidenceVisa === "optional";

  return (
    <form className={cn(className)} onSubmit={form.handleSubmit(submitHandler)}>
      <BodyContainer className="">
        <FormGroup controller={{ control: form.control, name: "type" }}>
          <Controller
            control={form.control}
            rules={{ required: true }}
            name="type"
            render={(args) => (
              <>
                <ListBox
                  vSize="lg"
                  vColor={formControlColorState(args.fieldState)}
                  options={typeOptions}
                  selectedOption={
                    args.field.value ? typeOptionsMap[args.field.value] : null
                  }
                  onOptionChange={(option) => {
                    args.field.onChange(option?.id);
                    form.setValue("tempResidenceVisa", undefined);
                    form.setValue("documentNumber", "");
                    form.setValue("issuingCountry", undefined);
                    form.setValue("expiry", undefined);
                    form.setValue("issueDate", undefined);
                  }}
                >
                  <ListBoxLabel required>Type</ListBoxLabel>
                </ListBox>
                <FormError>
                  {" "}
                  {formErrorMessage(args.fieldState.error, {
                    required: "Required",
                  })}{" "}
                </FormError>
              </>
            )}
          />
        </FormGroup>
        <FormGroup
          controller={{ control: form.control, name: "documentNumber" }}
        >
          <Label required>Document Number</Label>
          <Input
            vSize="lg"
            {...form.register("documentNumber", { required: true })}
          />
          <FormError>
            {formErrorMessage(form.formState.errors.documentNumber, {
              required: "Required",
            })}
          </FormError>
        </FormGroup>
        {showTempResidenceVisa && <FormGroup
          controller={{ control: form.control, name: "tempResidenceVisa" }}
        >
          <Label required={type?.tempResidenceVisa === "required"}>Temporary Residence Visa</Label>
          <Input
            vSize="lg"
            {...form.register("tempResidenceVisa", { required: type.tempResidenceVisa === "required", minLength: 2 })}
          />
          <FormError>
            {formErrorMessage(form.formState.errors.tempResidenceVisa, {
              required: "Required",
            })}
          </FormError>
        </FormGroup>}
        {showIssueDate || showExpiryDate ? (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {showIssueDate ? (
              <FormGroup
                controller={{ control: form.control, name: "issueDate" }}
              >
                <Controller
                  control={form.control}
                  rules={{
                    required: type?.issueDate === "required",
                    validate: (dateStr) => {
                      if (!dateStr) return;
                      const date = new Date(dateStr);
                      if (date.getTime() > Date.now()) {
                        return "Invalid issue date";
                      }
                    },
                  }}
                  name="issueDate"
                  render={(args) => (
                    <>
                      <DatePicker
                        label="Issue Date"
                        required={type?.issueDate === "required"}
                        vColor={formControlColorState(args.fieldState)}
                        vSize="lg"
                        value={args.field.value}
                        onChange={args.field.onChange}
                        isDateUnavailable={afterCurrentDateFilter}
                      />
                      <FormError>
                        {formErrorMessage(args.fieldState.error, {
                          required: "Required",
                        })}
                      </FormError>
                    </>
                  )}
                />
              </FormGroup>
            ) : null}
            {showExpiryDate ? (
              <FormGroup controller={{ control: form.control, name: "expiry" }}>
                <Controller
                  control={form.control}
                  rules={{
                    required: type?.expiryDate === "required",
                    validate: (dateStr) => {
                      if (!dateStr) return;
                      const date = new Date(dateStr);
                      if (date.getTime() < Date.now()) {
                        return "Invalid expiry";
                      }
                    },
                  }}
                  name="expiry"
                  render={(args) => (
                    <>
                      <DatePicker
                        vSize="lg"
                        vColor={formControlColorState(args.fieldState)}
                        label="Expiry Date"
                        required={type?.expiryDate === "required"}
                        value={args.field.value}
                        onChange={args.field.onChange}
                        isDateUnavailable={beforeCurrentDateFilter}
                      />
                      <FormError>
                        {formErrorMessage(args.fieldState.error, {
                          required: "Required",
                        })}
                      </FormError>
                    </>
                  )}
                />
              </FormGroup>
            ) : null}
          </div>
        ) : null}

        {showIssuingCountry ? (
          <FormGroup
            controller={{ control: form.control, name: "issuingCountry" }}
          >
            <Label required>Issuing Country</Label>
            <Controller
              control={form.control}
              rules={{ required: type?.issuingCountry === "required" }}
              name="issuingCountry"
              render={(args) => (
                <>
                  <IssuingCountryComboBox
                    value={args.field.value || null}
                    options={countryOptions}
                    onChange={(x) => args.field.onChange(x)}
                    error={!!args.fieldState.error}
                  />
                  <FormError>
                    {formErrorMessage(args.fieldState.error, {
                      required: "Required",
                    })}
                  </FormError>
                </>
              )}
            />
          </FormGroup>
        ) : null}
        <Button
          className="mt-10 w-full"
          type="submit"
          loading={form.formState.isSubmitting}
          vSize="lg"
        >
          Continue
        </Button>
      </BodyContainer>
    </form>
  );
}
